import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, TrashSimple } from '@phosphor-icons/react';
import { useEndpoints } from '../../../../hooks/useEndpoints';
import {
  IFieldWorkoutBlock,
  IFieldBlockExercise,
  TacticalPhase,
} from '../../../../models/field-programming';
import styles from '../FieldWorkoutBuilderModal.module.scss';

interface Props {
  blocks: IFieldWorkoutBlock[];
  onChange: (b: IFieldWorkoutBlock[]) => void;
}

// NOTE: 4 dei valori enum (activation/athletic/technical/compensative) non hanno
// ancora un colore definito nel dominio — accesso reso difensivo via Partial.
const PHASE_COLORS: Partial<Record<TacticalPhase, string>> = {
  warmup: '#f59e0b',
  situational: '#6366f1',
  match: '#10b981',
  cool_down: '#0ea5e9',
};

const ExercisesStep: FC<Props> = ({ blocks, onChange }) => {
  const api = useEndpoints();
  const exQ = useQuery({
    queryKey: ['fieldProgramming', 'exercises', { limit: 100 }],
    queryFn: () =>
      api.fieldProgramming.exercises.list({ limit: 100 }).then((r) => r.data.docs),
  });

  const updateBlockExercises = (
    bIdx: number,
    exercises: IFieldBlockExercise[],
  ) => onChange(blocks.map((b, i) => (i === bIdx ? { ...b, exercises } : b)));

  const addEx = (bIdx: number) => {
    const block = blocks[bIdx];
    updateBlockExercises(bIdx, [
      ...block.exercises,
      { customName: '', durationMinutes: 5, order: block.exercises.length },
    ]);
  };

  const updEx = (
    bIdx: number,
    exIdx: number,
    patch: Partial<IFieldBlockExercise>,
  ) => {
    const exs = blocks[bIdx].exercises.map((e, i) =>
      i === exIdx ? { ...e, ...patch } : e,
    );
    updateBlockExercises(bIdx, exs);
  };

  const rmEx = (bIdx: number, exIdx: number) => {
    const exs = blocks[bIdx].exercises
      .filter((_, i) => i !== exIdx)
      .map((e, i) => ({ ...e, order: i }));
    updateBlockExercises(bIdx, exs);
  };

  if (blocks.length === 0) {
    return (
      <div className={styles.emptyHint}>
        Nessun blocco. Torna allo step precedente per aggiungerne uno.
      </div>
    );
  }

  return (
    <div className={styles.exercisesNestedList}>
      {blocks.map((b, bIdx) => {
        const phaseColor = PHASE_COLORS[b.tacticalPhase];
        return (
          <div key={bIdx} className={styles.nestedBlockCard}>
            <div className={styles.blockColorStripInline} style={{ background: phaseColor }} />
            <div className={styles.nestedBlockBody}>
              <div className={styles.nestedBlockHeader}>
                <p className={styles.nestedBlockTitle}>
                  B{bIdx + 1}. {b.snapshotName}
                </p>
                <span className={styles.exerciseSets}>
                  {b.exercises.length} eserciz{b.exercises.length === 1 ? 'io' : 'i'}
                </span>
              </div>

              <div className={styles.exercisesList}>
                {b.exercises.map((ex, exIdx) => (
                  <div key={exIdx} className={styles.exerciseCard}>
                    <div className={styles.exerciseTopRow}>
                      <span className={styles.exerciseOrder}>{exIdx + 1}</span>
                      <select
                        className={styles.exerciseSelect}
                        value={ex.fieldExerciseId ?? ''}
                        onChange={(e) =>
                          updEx(bIdx, exIdx, {
                            fieldExerciseId: e.target.value || undefined,
                            customName: e.target.value ? undefined : ex.customName,
                          })
                        }
                      >
                        <option value="">— Inline (custom) —</option>
                        {exQ.data?.map((fe) => (
                          <option key={fe._id} value={fe._id}>
                            {fe.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className={styles.deleteExerciseBtn}
                        onClick={() => rmEx(bIdx, exIdx)}
                        title="Rimuovi esercizio"
                      >
                        <TrashSimple size={15} />
                      </button>
                    </div>

                    {!ex.fieldExerciseId && (
                      <div className={styles.exerciseParamsRow}>
                        <div className={styles.paramField} style={{ flex: 2 }}>
                          <label className={styles.paramLabel}>Nome custom</label>
                          <input
                            className={styles.paramInput}
                            placeholder="Es. Possesso 4v4"
                            value={ex.customName ?? ''}
                            onChange={(e) =>
                              updEx(bIdx, exIdx, { customName: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    )}

                    <div className={styles.exerciseParamsRow}>
                      <div className={styles.paramField}>
                        <label className={styles.paramLabel}>Durata (min)</label>
                        <input
                          type="number"
                          min={0}
                          className={styles.paramInput}
                          placeholder="—"
                          value={ex.durationMinutes ?? ''}
                          onChange={(e) =>
                            updEx(bIdx, exIdx, {
                              durationMinutes: e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            })
                          }
                        />
                      </div>

                      <div className={styles.paramField}>
                        <label className={styles.paramLabel}>Intensità (1-10)</label>
                        <input
                          type="number"
                          min={1}
                          max={10}
                          className={styles.paramInput}
                          placeholder="—"
                          value={ex.intensity ?? ''}
                          onChange={(e) =>
                            updEx(bIdx, exIdx, {
                              intensity: e.target.value
                                ? Number(e.target.value)
                                : undefined,
                            })
                          }
                        />
                      </div>

                      <div className={styles.paramField} style={{ minWidth: 120 }}>
                        <label className={styles.paramLabel}>Organizzazione</label>
                        <input
                          className={styles.paramInput}
                          placeholder="Es. 4v4"
                          value={ex.organization ?? ''}
                          onChange={(e) =>
                            updEx(bIdx, exIdx, { organization: e.target.value })
                          }
                        />
                      </div>

                      <div className={styles.paramField} style={{ flex: 2 }}>
                        <label className={styles.paramLabel}>Note</label>
                        <input
                          className={styles.paramInput}
                          placeholder="Note aggiuntive..."
                          value={ex.notes ?? ''}
                          onChange={(e) =>
                            updEx(bIdx, exIdx, { notes: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className={styles.addExerciseBtn}
                onClick={() => addEx(bIdx)}
              >
                <Plus size={14} weight="bold" />
                Aggiungi esercizio
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExercisesStep;
